import './TQServiceCard.scss';

import React, { useMemo } from 'react';

import { Typography } from 'antd';
import moment from 'moment';
import { PiArrowDownRightBold } from 'react-icons/pi';
import TMIcon from 'tm-icons-library';

import { useTheme } from '../../../../context/ThemeProvider';
import { useAppSelector } from '../../../../shared/hooks';
import {
  checkIfCurrentLoggedInUser,
  getReadableDateAndTimeFormat,
  getSingularOrPlural,
  getUserDisplayName,
} from '../../../../shared/utils';
import {
  ITQServiceRequest,
  TQServiceRequestTypeValue,
} from '../../../../types/tqConnectTypes/service-requests';

const ICON_SIZE = 16;

interface Props {
  requestType: TQServiceRequestTypeValue;
  serviceRequest: ITQServiceRequest;
  isMyRequest: boolean;
  isRequestedToMe: boolean;
  isRequestedToMePrivately: boolean;
}

const TQServiceRequestCardInfo: React.FC<Props> = ({
  requestType,
  serviceRequest,
  isMyRequest,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isRequestedToMe: _,
  isRequestedToMePrivately,
}) => {
  const { colors: COLORS } = useTheme();

  const {
    id: userId,
    name: userName,
    profilePic: userProfilePicUrl,
  } = useAppSelector((state) => state.user);

  const {
    status,
    visibility,
    requestedBy,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    requestedTo: __,
    myResponse,
    latestResponse,
    totalResponses,
    createdAt,
    latestMessage,
  } = serviceRequest;

  const { type, icon, title, date } = useMemo(() => {
    if (
      visibility === 'private' &&
      status === 'open' &&
      !isRequestedToMePrivately
    ) {
      return {};
    }

    if (
      status === 'in-progress' &&
      latestMessage &&
      !checkIfCurrentLoggedInUser(latestMessage?.sender?._id)
    ) {
      return {
        type: 'info',
        icon: (
          <TMIcon name="chatbubble" size={ICON_SIZE} color={COLORS.INFO_ALT} />
        ),
        title: (
          <>
            <Typography.Text strong>You</Typography.Text> recently received a
            message from{' '}
            <Typography.Text strong>
              {getUserDisplayName(
                latestMessage?.sender?._id,
                latestMessage?.sender?.name,
              )}
            </Typography.Text>
          </>
        ),
        date: latestMessage?.createdAt,
      };
    }

    if (isRequestedToMePrivately) {
      return {
        type: 'warning',
        icon: <PiArrowDownRightBold size={ICON_SIZE} color={COLORS.ICON} />,
        title: (
          <>
            <Typography.Text strong>You</Typography.Text> received a request
            from{' '}
            <Typography.Text strong>
              {getUserDisplayName(requestedBy?._id, requestedBy?.name)}
            </Typography.Text>{' '}
            to help
          </>
        ),
      };
    }

    const response =
      requestType === 'responded'
        ? {
            ...myResponse,
            respondedBy: {
              _id: userId as string,
              name: userName,
              profilePicUrl: userProfilePicUrl,
            },
          }
        : latestResponse;

    // In Requests Raised Tab: show latest response info based on latest response status
    if (isMyRequest && response) {
      if (
        // (visibility === 'public' && status === 'in-progress') ||
        status === 'completed'
      ) {
        return {};
      }

      const respondedBy =
        visibility === 'private' ? response.respondedBy : requestedBy;
      const respondedTo =
        visibility === 'private' ? requestedBy : response.respondedBy;

      if (response.status === 'rejected') {
        return {
          type: 'error',
          icon: (
            <TMIcon name="close-circle" size={ICON_SIZE} color={COLORS.ERROR} />
          ),
          title: (
            <>
              {visibility === 'private' ? 'Sorry! ' : ''}
              <Typography.Text strong>
                {getUserDisplayName(respondedBy?._id, respondedBy?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              rejected{' '}
              <Typography.Text strong>
                {getUserDisplayName(respondedTo?._id, respondedTo?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'your',
                  suffix: "'s",
                })}
              </Typography.Text>{' '}
              {visibility === 'private' ? 'request' : 'offer'}
            </>
          ),
          date: response?.updatedAt,
        };
      }

      if (response.status === 'accepted') {
        return {
          type: 'success',
          icon: (
            <TMIcon
              name="checkmark-circle"
              size={ICON_SIZE}
              color={COLORS.SUCCESS}
            />
          ),
          title: (
            <>
              {visibility === 'private' ? 'Yeah! ' : ''}
              <Typography.Text strong>
                {getUserDisplayName(respondedBy?._id, respondedBy?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              accepted{' '}
              <Typography.Text strong>
                {getUserDisplayName(respondedTo?._id, respondedTo?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'your',
                  suffix: "'s",
                })}
              </Typography.Text>{' '}
              {visibility === 'private' ? 'request' : 'offer'}
            </>
          ),
          date: response?.updatedAt,
        };
      }

      return {
        type: 'info',
        icon: (
          <TMIcon name="pricetag" size={ICON_SIZE} color={COLORS.INFO_ALT} />
        ),
        title: (
          <>
            <Typography.Text strong>
              {getUserDisplayName(
                response?.respondedBy?._id,
                response?.respondedBy?.name || '',
              )}
            </Typography.Text>{' '}
            offered help
          </>
        ),
        date: response?.updatedAt,
      };
    }

    // In Help Offered Tab: show my response info based on my help offer response status
    if (requestType === 'responded' && response) {
      const respondedBy =
        visibility === 'private' ? response.respondedBy : requestedBy;
      const respondedTo =
        visibility === 'private' ? requestedBy : response.respondedBy;

      if (response.status === 'rejected') {
        return {
          type: 'error',
          icon: (
            <TMIcon name="close-circle" size={ICON_SIZE} color={COLORS.ERROR} />
          ),
          title: (
            <>
              {visibility === 'private' ? 'Sorry! ' : ''}
              <Typography.Text strong>
                {getUserDisplayName(respondedBy?._id, respondedBy?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              rejected{' '}
              <Typography.Text strong>
                {getUserDisplayName(respondedTo?._id, respondedTo?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'your',
                  suffix: "'s",
                })}
              </Typography.Text>{' '}
              offer
            </>
          ),
          date: response?.updatedAt,
        };
      }

      if (response.status === 'accepted') {
        return {
          type: 'success',
          icon: (
            <TMIcon
              name="checkmark-circle"
              size={ICON_SIZE}
              color={COLORS.SUCCESS}
            />
          ),
          title: (
            <>
              {visibility === 'private' ? 'Yeah! ' : ''}
              <Typography.Text strong>
                {getUserDisplayName(respondedBy?._id, respondedBy?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              accepted{' '}
              <Typography.Text strong>
                {getUserDisplayName(respondedTo?._id, respondedTo?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'your',
                  suffix: "'s",
                })}
              </Typography.Text>{' '}
              offer
            </>
          ),
          date: response?.updatedAt,
        };
      }
    }

    if (
      isMyRequest &&
      totalResponses > 1 &&
      !moment(latestResponse?.createdAt).isSame(moment(), 'day')
    ) {
      return {
        type: 'info',
        icon: (
          <TMIcon name="pricetags" size={ICON_SIZE} color={COLORS.INFO_ALT} />
        ),
        title: (
          <>
            <Typography.Text strong>You</Typography.Text> received{' '}
            <Typography.Text strong>
              {totalResponses > 10 ? '10+' : totalResponses}{' '}
              {getSingularOrPlural(totalResponses, 'offer')}
            </Typography.Text>
          </>
        ),
      };
    }

    return {};
  }, [
    COLORS.ERROR,
    COLORS.ICON,
    COLORS.INFO_ALT,
    COLORS.SUCCESS,
    isMyRequest,
    isRequestedToMePrivately,
    myResponse,
    requestType,
    latestResponse,
    requestedBy,
    status,
    totalResponses,
    visibility,
    userId,
    userName,
    userProfilePicUrl,
    latestMessage,
  ]);

  if (!icon || !title) {
    return null;
  }

  return (
    <div
      className="card__info"
      style={{
        backgroundColor:
          (type === 'error' && COLORS.ERROR100) ||
          (type === 'success' && COLORS.SUCCESS100) ||
          (type === 'info' && COLORS.INFO100) ||
          (type === 'warning' && COLORS.WARNING200) ||
          COLORS.SURFACE_PAGE,
      }}>
      <span
        className="info__icon"
        style={{
          flexShrink: 0,
          marginRight: 6,
          display: 'inline-flex',
          alignItems: 'center',
        }}>
        {icon}
      </span>

      <Typography.Text className="info__text">{title}</Typography.Text>

      <Typography.Text
        type="secondary"
        style={{
          fontSize: 12,
        }}
        className="info__extra">
        &nbsp;•&nbsp;
        {getReadableDateAndTimeFormat(date || createdAt)}
      </Typography.Text>
    </div>
  );
};

export default TQServiceRequestCardInfo;
